import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import {
  FaEye, FaLink, FaUserPlus, FaUsers, FaDownload, FaPen, FaClone, FaInfoCircle,
  FaTrashAlt, FaTrash, FaEdit, FaGoogleDrive,
} from 'react-icons/fa';
import { ONLYOFFICE_SUPPORTED_TYPES, ONLYOFFICE_EDITABLE_TYPES } from 'components/app/drive/hooks/useFileIcon';

const MenuItem = ({ icon: Icon, label, onClick, danger = false, divider = false }) => (
  <>
    {divider && <div className="tw-h-px tw-bg-slate-100 tw-my-1" />}
    <button
      type="button"
      onClick={onClick}
      className={`tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-bg-transparent tw-border-0 tw-text-left tw-cursor-pointer hover:tw-bg-slate-100 tw-transition-colors ${danger ? 'tw-text-red-600 hover:tw-bg-red-50' : 'tw-text-slate-700'}`}
    >
      <Icon className={`${danger ? 'tw-text-red-500' : 'tw-text-slate-400'} tw-shrink-0`} size={12} />
      <span>{label}</span>
    </button>
  </>
);

MenuItem.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  danger: PropTypes.bool,
  divider: PropTypes.bool,
};

const DriveItemMenu = ({ item, anchorRef, onClose, handleAction }) => {
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const isFile = item?.asset_type === 'file';
  const isPublic = item?.public;
  const canOpenInOnlyOffice = isFile && ONLYOFFICE_SUPPORTED_TYPES.includes(item?.content_type);
  const canEditInOnlyOffice = isFile && ONLYOFFICE_EDITABLE_TYPES.includes(item?.content_type);
  const {
    allow_view, allow_edit, allow_delete, allow_copy, allow_share,
    allow_make_public, allow_move_to_trash, allow_download,
  } = item?.permissions || {};

  useEffect(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const menuWidth = 220;
    const menuHeight = 320;
    let left = rect.right - menuWidth;
    let top = rect.bottom + 4;
    if (left < 8) left = rect.left;
    if (top + menuHeight > window.innerHeight) top = rect.top - menuHeight - 4;
    setPos({ top, left });
  }, [anchorRef]);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    const handleEsc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  const run = (action) => {
    onClose();
    handleAction(item, action);
  };

  const menu = (
    <div
      ref={menuRef}
      className="tw-fixed tw-bg-white tw-rounded tw-shadow-lg tw-border tw-border-slate-200 tw-py-1 tw-w-[220px] tw-overflow-y-auto"
      style={{ top: pos.top, left: pos.left, zIndex: 100001, maxHeight: '320px' }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* View group */}
      {allow_view && isFile && <MenuItem icon={FaEye}     label="Preview"        onClick={() => run('preview')} />}
      {canOpenInOnlyOffice    && <MenuItem icon={FaGoogleDrive} label="View online"     onClick={() => run('view-online')} />}
      {canEditInOnlyOffice && allow_edit && <MenuItem icon={FaEdit} label="Edit online" onClick={() => run('edit-online')} />}

      <MenuItem icon={FaInfoCircle}  label="Details"    onClick={() => run('detail')} divider />

      {/* Sharing group — all gated by allow_share */}
      {allow_share                                       && <MenuItem icon={FaLink}     label="Get link"    onClick={() => run('getLink')}    divider />}
      {allow_share && !isPublic                          && <MenuItem icon={FaUserPlus} label="Share"       onClick={() => run('share')} />}
      {allow_share && !isPublic && allow_make_public     && <MenuItem icon={FaUsers}    label="Make public" onClick={() => run('makePublic')} />}

      {/* File ops group */}
      {allow_download && <MenuItem icon={FaDownload} label="Download" onClick={() => run('download')} divider />}
      {allow_edit     && <MenuItem icon={FaPen}      label="Rename"   onClick={() => run('rename')} />}
      {allow_copy && isFile && <MenuItem icon={FaClone} label="Make a copy" onClick={() => run('copy')} />}

      {/* Danger group */}
      {allow_move_to_trash && <MenuItem icon={FaTrashAlt} label="Move to trash" onClick={() => run('moveToTrash')} divider />}
      {allow_delete        && <MenuItem icon={FaTrash}    label="Delete forever" onClick={() => run('deleteForever')} danger />}
    </div>
  );

  return createPortal(menu, document.body);
};

DriveItemMenu.propTypes = {
  item: PropTypes.object.isRequired,
  anchorRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  onClose: PropTypes.func.isRequired,
  handleAction: PropTypes.func.isRequired,
};

export default DriveItemMenu;
